import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── CR-14: Trade-specific field-schema fetch + in-memory cache ──────────
// Resolves the dynamic field list the UI renders for a given trade at
// customer intake, the ticket, and the invoice. Cached per-trade so the
// schema endpoint is hit at most once per trade per session.
const _fieldSchemaCache = {};

export const fetchTradeFieldSchema = async (trade) => {
  const key = (trade || '').trim() || '__general__';
  if (_fieldSchemaCache[key]) return _fieldSchemaCache[key];
  try {
    const res = await axios.get(
      `${API}/industries/trade/${encodeURIComponent(trade || 'General Service')}/field-schema`
    );
    _fieldSchemaCache[key] = res.data;
    return res.data;
  } catch (e) {
    return { trade, group: 'general_service', shows_appliance_fields: false, is_fallback: true, fields: [] };
  }
};

// Default terminology (fallback)
const DEFAULT_TERMINOLOGY = {
  job: "Job",
  jobs_plural: "Jobs",
  customer: "Customer",
  customers_plural: "Customers",
  worker: "Technician",
  workers_plural: "Technicians",
  parts: "Parts",
  invoice: "Invoice",
  diagnosis: "Diagnosis",
  schedule: "Schedule",
  completed_job: "Completed"
};

// Create context
const TradeContext = createContext({
  trade: null,
  terminology: DEFAULT_TERMINOLOGY,
  features: {},
  aiContext: "",
  loading: true,
  setTrade: () => {}
});

// Provider component
export const TradeProvider = ({ children }) => {
  const [trade, setTrade] = useState(null);
  const [terminology, setTerminology] = useState(DEFAULT_TERMINOLOGY);
  const [features, setFeatures] = useState({});
  const [aiContext, setAiContext] = useState("");
  const [loading, setLoading] = useState(true);

  // Load trade from localStorage on mount
  useEffect(() => {
    const savedTrade = localStorage.getItem('userTrade');
    if (savedTrade) {
      setTrade(savedTrade);
    } else {
      setLoading(false);
    }
  }, []);

  // Fetch trade profile when trade changes
  useEffect(() => {
    const fetchTradeProfile = async () => {
      if (!trade) {
        setTerminology(DEFAULT_TERMINOLOGY);
        setFeatures({});
        setAiContext("");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await axios.get(`${API}/trade-profiles/${encodeURIComponent(trade)}`);
        const profile = response.data.profile;

        if (profile) {
          // Set terminology
          if (profile.terminology) {
            setTerminology({
              ...DEFAULT_TERMINOLOGY,
              ...profile.terminology
            });
          }

          // Set features
          if (profile.features) {
            setFeatures(profile.features);
          }

          // Set AI context
          if (profile.ai_context) {
            setAiContext(JSON.stringify(profile.ai_context));
          }
        }

        // Save to localStorage
        localStorage.setItem('userTrade', trade);
      } catch (err) {
        console.error('Error fetching trade profile:', err);
        setTerminology(DEFAULT_TERMINOLOGY);
      } finally {
        setLoading(false);
      }
    };

    fetchTradeProfile();
  }, [trade]);

  // Helper function to get terminology with fallback
  const t = (key) => {
    return terminology[key] || DEFAULT_TERMINOLOGY[key] || key;
  };

  // Check if a feature is enabled for this trade
  const hasFeature = (featureKey) => {
    // If no features loaded, default to true (show all)
    if (Object.keys(features).length === 0) return true;
    return features[featureKey] !== false;
  };

  return (
    <TradeContext.Provider 
      value={{ 
        trade, 
        setTrade, 
        terminology, 
        features,
        aiContext,
        loading,
        t,  // Terminology helper
        hasFeature,  // Feature flag helper
        getFieldSchema: fetchTradeFieldSchema  // CR-14: dynamic trade field schema (cached)
      }}
    >
      {children}
    </TradeContext.Provider>
  );
};

// Hook to use trade context
export const useTrade = () => {
  const context = useContext(TradeContext);
  if (!context) {
    throw new Error('useTrade must be used within a TradeProvider');
  }
  return context;
};

// Hook for just terminology (convenience)
export const useTerminology = () => {
  const { t, terminology } = useTrade();
  return { t, terminology };
};

// Hook for feature flags (convenience)
export const useFeatureFlags = () => {
  const { hasFeature, features } = useTrade();
  return { hasFeature, features };
};

export default TradeContext;
